import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Download, ChevronDown, ChevronRight, FileText } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GradientButton, GhostButton, ConfidenceBar } from "@/components/primitives";

const LIKELIHOOD = {
  high: "bg-red-500/10 text-red-300 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  low: "bg-blue-500/10 text-blue-300 border-blue-500/30",
};

export default function StructuredRCA({ incident }) {
  const [data, setData] = useState(incident.rca_structured || null);
  const [busy, setBusy] = useState(false);
  const [exec, setExec] = useState("");
  const [execBusy, setExecBusy] = useState(false);
  const [openCauses, setOpenCauses] = useState({});

  const generate = async () => {
    setBusy(true);
    try { const { data: r } = await api.post(`/incidents/${incident.id}/rca-structured`); setData(r); }
    finally { setBusy(false); }
  };

  const generateExec = async () => {
    setExecBusy(true);
    try { const { data: r } = await api.post(`/incidents/${incident.id}/executive-report`); setExec(r.report); }
    finally { setExecBusy(false); }
  };

  const download = () => {
    if (!data) return;
    const parts = [
      `# Root Cause Analysis — ${incident.title}`,
      `\n_Generated by SentinelAI · confidence ${Math.round((data.confidence||0)*100)}%_\n`,
      `## Executive Summary\n${data.executive_summary || ""}\n`,
      `## Problem Summary\n${data.problem_summary || ""}\n`,
      `## Affected Services\n${(data.affected_services||[]).map(s=>`- ${s}`).join("\n")}\n`,
      `## Timeline\n${data.timeline_summary || ""}\n`,
      `## Possible Root Causes\n${(data.possible_causes||[]).map(c=>`- **${c.cause}** _(likelihood: ${c.likelihood})_\n  Evidence: ${c.evidence}`).join("\n")}\n`,
      `## Suggested Resolution\n${(data.suggested_resolution||[]).map((s,i)=>`${i+1}. ${s}`).join("\n")}\n`,
      `## Preventive Actions\n${(data.preventive_actions||[]).map(s=>`- ${s}`).join("\n")}\n`,
      `## Estimated Time to Resolve\n${data.estimated_time_to_resolve_min} minutes\n`,
      exec ? `## Executive Report\n${exec}\n` : "",
    ];
    const blob = new Blob([parts.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rca-${incident.id}.md`; a.click();
  };

  if (!data) {
    return (
      <GlassCard className="p-6" data-testid="structured-rca-empty">
        <SectionLabel>Root Cause Analysis</SectionLabel>
        <div className="mt-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-sm text-slate-400 flex items-center justify-between gap-3 flex-wrap">
          <span><Sparkles className="inline h-4 w-4 text-cyan-300 mr-1" /> SentinelAI can analyze this incident and produce a structured RCA with possible causes, evidence and a remediation plan.</span>
          <GradientButton onClick={generate} disabled={busy} data-testid="structured-rca-generate"><Sparkles className="h-4 w-4" /> {busy ? "Analyzing…" : "Generate structured RCA"}</GradientButton>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6" data-testid="structured-rca">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <SectionLabel>Root Cause Analysis · structured</SectionLabel>
          <h3 className="mt-1 text-lg font-semibold">AI-generated</h3>
        </div>
        <div className="flex gap-2">
          <GhostButton onClick={generate} disabled={busy} data-testid="structured-rca-regenerate"><Sparkles className="h-4 w-4" /> Regenerate</GhostButton>
          <GhostButton onClick={generateExec} disabled={execBusy} data-testid="structured-rca-exec">{execBusy ? "Drafting…" : "Executive report"}</GhostButton>
          <GradientButton onClick={download} data-testid="structured-rca-download"><Download className="h-4 w-4" /> Download</GradientButton>
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">AI Confidence</div>
          <div className="mt-1 text-2xl font-mono font-semibold gradient-text">{Math.round((data.confidence||0)*100)}%</div>
          <div className="mt-2"><ConfidenceBar value={data.confidence || 0.8} /></div>
        </div>
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">ETA</div>
          <div className="mt-1 text-2xl font-mono font-semibold">{data.estimated_time_to_resolve_min}<span className="text-sm text-slate-400 ml-1">min</span></div>
        </div>
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Affected services</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(data.affected_services || []).map((s) => (
              <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">{s}</span>
            ))}
          </div>
        </div>
      </div>

      <Block title="Executive Summary"><p className="text-sm text-slate-300 leading-relaxed">{data.executive_summary}</p></Block>
      <Block title="Problem Summary"><p className="text-sm text-slate-300 leading-relaxed">{data.problem_summary}</p></Block>
      <Block title="Timeline"><p className="text-sm text-slate-300 whitespace-pre-line">{data.timeline_summary}</p></Block>

      <Block title="Possible Root Causes">
        <div className="space-y-2">
          {(data.possible_causes || []).map((c, i) => (
            <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10">
              <button onClick={() => setOpenCauses((o) => ({ ...o, [i]: !o[i] }))} className="w-full flex items-center justify-between p-3 text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {openCauses[i] ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                  <span className="text-sm truncate">{c.cause}</span>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${LIKELIHOOD[(c.likelihood||"medium").toLowerCase()] || LIKELIHOOD.medium}`}>{c.likelihood}</span>
              </button>
              {openCauses[i] && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-3 pb-3 text-sm text-slate-400 border-t border-white/5 pt-3">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Evidence</span>
                  <p className="mt-1 leading-relaxed">{c.evidence}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </Block>

      <Block title="Suggested Resolution">
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-300">
          {(data.suggested_resolution || []).map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </Block>

      <Block title="Preventive Actions">
        <ul className="list-disc list-inside space-y-1 text-sm text-slate-300">
          {(data.preventive_actions || []).map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </Block>

      {exec && (
        <Block title="Executive Report">
          <div className="text-sm text-slate-300 whitespace-pre-wrap">{exec}</div>
        </Block>
      )}
    </GlassCard>
  );
}

function Block({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
